import React, { useState, useMemo, useEffect } from 'react';
import { Button, Empty, Spin } from 'antd';
import { InfoCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgent, getSelectedAgentId } from '@/store/account/selector';
import { DEFAULT_ACTIVE_KEYS, UI_TEXT, WORKFLOW_CONFIG, WORKFLOW_TYPES } from 'src/enums/EOutbound';
import { WorkflowSection } from './Workflows/WorkflowSection';
import { getWorkflowIcon, parseAgentWorkflows } from './Workflows/WorkflowHelper';
import { CustomWorkflowForm } from './Workflows/WorkflowForm';
import { useOutbound } from 'src/Hooks/SmartCRM/useOutbound';

const WorkflowsStep: React.FC = () => {
	const selectedAgent = useAppSelector(getSelectedAgent);
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const { currentCampaign, updateCurrentCampaign, getWorkflowsMutation } = useOutbound();
	const [activeKeys, setActiveKeys] = useState<string[]>(DEFAULT_ACTIVE_KEYS);
	const [customActiveKeys, setCustomActiveKeys] = useState<string[]>([]);
	const [showForm, setShowForm] = useState<string | null>(null);
	const [customWorkflows, setCustomWorkflows] = useState<any[]>([]);

	const agentWorkflows = useMemo(() => {
		return parseAgentWorkflows(selectedAgent);
	}, [selectedAgent]);

	const customWorkflowsByType = useMemo(() => {
		if (!customWorkflows || customWorkflows.length === 0) {
			return {};
		}

		const grouped: { [key: string]: any[] } = {};

		customWorkflows.forEach((workflow) => {
			if (!workflow || !workflow.type) {
				return;
			}

			const workflowType = workflow.type.toLowerCase();

			if (!grouped[workflowType]) {
				grouped[workflowType] = [];
			}

			const formattedWorkflow = {
				id: workflow.id,
				name: workflow.name || 'Unnamed Workflow',
				description: workflow.scenario || '',
				enabled: workflow.enabled !== false,
				isCustom: true,
				customScenario: workflow.scenario,
				data: workflow.data || {},
				...workflow,
			};

			grouped[workflowType].push(formattedWorkflow);
		});

		return grouped;
	}, [customWorkflows]);

	useEffect(() => {
		if (selectedAgentId) {
			getWorkflowsMutation.mutate(selectedAgentId, {
				onSuccess: (data) => {
					if (data?.workflows) {
						setCustomWorkflows(data.workflows);
					}
				},
				onError: () => {
					setCustomWorkflows([]);
				},
			});
		}
	}, [selectedAgentId]);

	const handleWorkflowToggle = (workflowId: string, workflowType: string, enabled: boolean) => {
		const currentWorkflows = currentCampaign?.workflows || [];

		if (enabled) {
			let selectedWorkflow = null;
			let isCustomWorkflow = false;

			if (agentWorkflows[workflowType]) {
				selectedWorkflow = agentWorkflows[workflowType].find((w: any) => w.id === workflowId);
			}

			if (!selectedWorkflow && customWorkflowsByType[workflowType]) {
				selectedWorkflow = customWorkflowsByType[workflowType].find(
					(w: any) => w.id === workflowId,
				);
				isCustomWorkflow = true;
			}

			if (selectedWorkflow) {
				const typeMapping: { [key: string]: string } = {
					[WORKFLOW_TYPES.INTAKE]: 'Intake Form',
					[WORKFLOW_TYPES.SMS]: 'Text',
					[WORKFLOW_TYPES.TRANSFER]: 'Call Transfer',
				};

				const newWorkflow = {
					id: workflowId,
					type: typeMapping[workflowType] || workflowType,
					createdFor: isCustomWorkflow ? 'Outbound' : 'Inbound',
					name: selectedWorkflow.name,
				};

				const workflowExists = currentWorkflows.some((w) => w.id === workflowId);

				if (!workflowExists) {
					const updatedWorkflows = [...currentWorkflows, newWorkflow];
					updateCurrentCampaign({ workflows: updatedWorkflows });
				}
			}
		} else {
			const filteredWorkflows = currentWorkflows.filter((w) => w.id !== workflowId);
			updateCurrentCampaign({ workflows: filteredWorkflows });
		}
	};

	const handleAddWorkflow = (type: string) => {
		setShowForm(type);
	};

	const handleSaveWorkflow = () => {
		setShowForm(null);
		if (selectedAgentId) {
			getWorkflowsMutation.mutate(selectedAgentId, {
				onSuccess: (data) => {
					if (data?.workflows) {
						setCustomWorkflows(data.workflows);
					}
				},
			});
		}
	};

	const handleCancelForm = () => {
		setShowForm(null);
	};

	const getSelectedWorkflows = () => {
		const selected: { [key: string]: boolean } = {};

		if (!currentCampaign?.workflows) {
			return selected;
		}

		currentCampaign.workflows.forEach((campaignWorkflow) => {
			selected[campaignWorkflow.id] = true;
		});

		return selected;
	};

	const calculateSelectedCount = (workflowType: string) => {
		if (!currentCampaign?.workflows) {
			return 0;
		}

		let count = 0;
		const allWorkflows = agentWorkflows[workflowType] || [];

		allWorkflows.forEach((workflow) => {
			if (currentCampaign.workflows.some((cw) => cw.id === workflow.id)) {
				count++;
			}
		});

		return count;
	};

	const calculateCustomSelectedCount = (workflowType: string) => {
		if (!currentCampaign?.workflows) {
			return 0;
		}

		let count = 0;
		const customWorkflowsOfType = customWorkflowsByType[workflowType] || [];

		customWorkflowsOfType.forEach((workflow) => {
			if (currentCampaign.workflows.some((cw) => cw.id === workflow.id)) {
				count++;
			}
		});

		return count;
	};

	if (!selectedAgent) {
		return (
			<div className="w-full flex flex-col items-center justify-center py-12">
				<Empty description={UI_TEXT.NO_AGENT_SELECTED} />
			</div>
		);
	}

	const isLoadingCustomWorkflows = getWorkflowsMutation.isPending;

	return (
		<div className="w-full max-w-4xl mx-auto px-4 py-6">
			<div className="text-center mb-8">
				<h2 className="text-2xl font-bold text-gray-900 mb-2">{UI_TEXT.TITLE}</h2>
				<p className="text-gray-600 max-w-2xl mx-auto">{UI_TEXT.SUBTITLE}</p>
			</div>

			<div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
				<div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
					<h3 className="text-lg font-semibold text-gray-900">Agent Inbound Workflows</h3>
					<p className="text-sm text-gray-600 mt-1">
						Pre-configured workflows from your agent for inbound campaigns
					</p>
				</div>
				{Object.values(WORKFLOW_TYPES).map((type) => {
					const selectedCount = calculateSelectedCount(type);
					const totalCount = agentWorkflows[type]?.length || 0;

					return (
						<WorkflowSection
							key={type}
							workflows={agentWorkflows[type] || []}
							type={type}
							selectedWorkflows={getSelectedWorkflows()}
							onWorkflowToggle={handleWorkflowToggle}
							icon={getWorkflowIcon(type)}
							activeKeys={activeKeys}
							setActiveKeys={setActiveKeys}
							selectedCount={selectedCount}
							totalCount={totalCount}
						/>
					);
				})}
			</div>

			<div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mt-6">
				<div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 border-b border-gray-200">
					<div className="flex items-center justify-between">
						<div>
							<h3 className="text-lg font-semibold text-gray-900">Custom Outbound Workflows</h3>
							<p className="text-sm text-gray-600 mt-1">
								Your custom workflows created for outbound campaigns
							</p>
						</div>
						{isLoadingCustomWorkflows && (
							<Spin indicator={<LoadingOutlined style={{ fontSize: 20 }} spin />} />
						)}
					</div>
				</div>

				{isLoadingCustomWorkflows ? (
					<div className="flex justify-center items-center py-12">
						<Spin size="large" />
						<span className="ml-3 text-gray-600">Loading custom workflows...</span>
					</div>
				) : Object.keys(customWorkflowsByType).length > 0 ? (
					Object.entries(customWorkflowsByType).map(([workflowType, workflows]) => {
						const config = WORKFLOW_CONFIG[workflowType as keyof typeof WORKFLOW_CONFIG];
						const selectedCount = calculateCustomSelectedCount(workflowType);

						if (!config) {
							return null;
						}

						return (
							<WorkflowSection
								key={`custom-${workflowType}`}
								workflows={workflows}
								type={workflowType}
								selectedWorkflows={getSelectedWorkflows()}
								onWorkflowToggle={handleWorkflowToggle}
								icon={getWorkflowIcon(workflowType)}
								activeKeys={customActiveKeys}
								setActiveKeys={setCustomActiveKeys}
								selectedCount={selectedCount}
								totalCount={workflows.length}
							/>
						);
					})
				) : (
					<div className="flex justify-center items-center py-8 text-gray-500">
						No custom workflows available
					</div>
				)}
			</div>

			<div className="mt-8 p-6 bg-gray-50 rounded-xl border border-gray-200">
				<div className="flex items-start gap-3">
					<InfoCircleOutlined className="text-blue-500 mt-1 flex-shrink-0" />
					<div>
						<h4 className="font-medium text-gray-900 mb-2">{UI_TEXT.NEED_CUSTOM_TITLE}</h4>
						<p className="text-sm text-gray-600 mb-4">{UI_TEXT.NEED_CUSTOM_DESC}</p>
						<div className="flex flex-wrap gap-2">
							{Object.values(WORKFLOW_TYPES).map((type) => {
								const config = WORKFLOW_CONFIG[type];
								const icon = getWorkflowIcon(type);

								return (
									<Button
										key={type}
										icon={icon}
										onClick={() => handleAddWorkflow(type)}
										className="flex items-center gap-2"
										disabled={isLoadingCustomWorkflows}
									>
										{config.addButtonText}
									</Button>
								);
							})}
						</div>
					</div>
				</div>
			</div>

			{showForm && (
				<CustomWorkflowForm
					type={showForm}
					onCancel={handleCancelForm}
					onSave={handleSaveWorkflow}
				/>
			)}
		</div>
	);
};

export default WorkflowsStep;
